export type GameId =
  | 'ear-quiz'
  | 'note-quiz'
  | 'rhythm-quiz'
  | 'mock-keyboard'
  | 'chord-quiz'
  | 'key-signature-quiz';

export type GameMeta = {
  id: GameId;
  title: string;
  shortTitle: string;
  lead: string;
  skill: string;
  color: 'blue' | 'mint' | 'peach' | 'gold';
};

export type GameLevel = 1 | 2 | 3 | 4;

export type QuizQuestion = {
  id: string;
  answer: string;
  choices: string[];
  prompt: string;
  subtitle?: string;
  levels?: GameLevel[];
  note?: NoteName;
  image?: string;
  rhythm?: string[];
  chord?: string[];
  keySignature?: string;
};

export type NoteName =
  | 'C3' | 'D3' | 'E3' | 'F3' | 'G3' | 'A3' | 'B3'
  | 'C4' | 'D4' | 'E4' | 'F4' | 'G4' | 'A4' | 'B4'
  | 'C5' | 'D5' | 'E5' | 'F5' | 'G5' | 'A5' | 'B5';

export const games: GameMeta[] = [
  {
    id: 'ear-quiz',
    title: '音当てクイズ',
    shortTitle: '音感',
    lead: '聞こえた音を選んで、耳と音名をつなげます。',
    skill: '耳で聴く',
    color: 'mint',
  },
  {
    id: 'note-quiz',
    title: '音符クイズ',
    shortTitle: '読譜',
    lead: '五線譜を見て、音名をすばやく読めるようにします。',
    skill: '譜面を読む',
    color: 'blue',
  },
  {
    id: 'rhythm-quiz',
    title: 'リズムクイズ',
    shortTitle: 'リズム',
    lead: '流れるリズムを聞いて、同じパターンを選びます。',
    skill: '拍を感じる',
    color: 'peach',
  },
  {
    id: 'mock-keyboard',
    title: 'もぎけんばん',
    shortTitle: '鍵盤',
    lead: '鍵盤を押して、音名と位置を自由に確認できます。',
    skill: '鍵盤に触れる',
    color: 'gold',
  },
  {
    id: 'chord-quiz',
    title: '和音テスト',
    shortTitle: '和音',
    lead: '主要三和音と展開形を、見て選ぶ練習です。',
    skill: '和音を見分ける',
    color: 'blue',
  },
  {
    id: 'key-signature-quiz',
    title: '調号テスト',
    shortTitle: '調号',
    lead: 'シャープ・フラットの数から、何調かを判断します。',
    skill: '調号を読む',
    color: 'mint',
  },
];

export const noteLabels: Record<string, string> = {
  C: 'ド',
  D: 'レ',
  E: 'ミ',
  F: 'ファ',
  G: 'ソ',
  A: 'ラ',
  B: 'シ',
};

export const whiteNotes: NoteName[] = [
  'C4', 'D4', 'E4', 'F4', 'G4', 'A4', 'B4',
  'C5', 'D5', 'E5', 'F5', 'G5',
];

export const keyboardNotes: NoteName[] = [
  'C3', 'D3', 'E3', 'F3', 'G3', 'A3', 'B3',
  'C4', 'D4', 'E4', 'F4', 'G4', 'A4', 'B4',
  'C5', 'D5', 'E5', 'F5', 'G5', 'A5', 'B5',
];

export function labelForNote(note: string) {
  const pitch = note.charAt(0);
  return `${noteLabels[pitch] || pitch} (${note})`;
}

export function noteAudioPath(note: string) {
  return `/shared/notes/${note}.mp3`;
}

export function noteImagePath(note: string) {
  const octave = Number(note.slice(1));
  const clef = octave <= 3 ? 'bass' : 'treble';
  return `/games/note-quiz/out-${clef}-${note}.preview.png`;
}

const rhythmData = [
  ['L1A', 1, [1, 1, 1, 1]],
  ['L1B', 1, [1, 0, 1, 0]],
  ['L1C', 1, [1, 1, 0, 1]],
  ['L1D', 1, [1, 0, 0, 1]],
  ['L2A', 2, [1, 0, 1, 0, 1, 0, 1, 0]],
  ['L2B', 2, [1, 1, 0, 1, 1, 0, 1, 0]],
  ['L2C', 2, [1, 0, 1, 1, 0, 1, 1, 0]],
  ['L2D', 2, [1, 1, 1, 0, 1, 0, 0, 1]],
  ['L3A', 3, [0, 1, 0, 1, 0, 1, 0, 1]],
  ['L3B', 3, [1, 1, 0, 0, 1, 0, 1, 0]],
  ['L3C', 3, [1, 0, 1, 0, 0, 1, 0, 1]],
  ['L3D', 3, [1, 0, 1, 1, 0, 0, 1, 0]],
  ['L4A', 4, [1, 0, 1, 0, 1, 1, 0, 1, 0, 1, 0, 1]],
  ['L4B', 4, [1, 1, 0, 1, 0, 1, 0, 0, 1, 0, 1, 0]],
  ['L4C', 4, [1, 0, 0, 1, 1, 0, 1, 0, 1, 0, 1, 0]],
  ['L4D', 4, [1, 0, 1, 1, 0, 1, 0, 1, 1, 0, 0, 1]],
] as const;

export const rhythmPatterns: QuizQuestion[] = rhythmData.map(([id, level, steps]) => ({
  id,
  answer: `パターン ${id}`,
  choices: [],
  prompt: '音をきいて、同じリズムをえらぼう',
  subtitle: `レベル${level}のリズムを耳で聞き分けよう`,
  levels: [level],
  rhythm: steps.map((step) => step ? '♩' : '𝄽'),
  image: `/shared/media/test003/${id}.png`,
}));

const chordKeys = [
  ['c-major', 'ハ長調'],
  ['g-major', 'ト長調'],
  ['f-major', 'ヘ長調'],
  ['d-major', 'ニ長調'],
  ['a-minor', 'イ短調'],
] as const;

const chordDegrees = [
  ['i', 'Ⅰ', '主和音'],
  ['iv', 'Ⅳ', '下属和音'],
  ['v', 'Ⅴ', '属和音'],
] as const;

const chordPositions = [
  ['root', '基本形'],
  ['first', '第1転回形'],
  ['second', '第2転回形'],
] as const;

function chordLevels(keyId: string, positionId: string): GameLevel[] {
  if (keyId === 'c-major' && positionId === 'root') return [1, 2, 3, 4];
  if (keyId === 'c-major') return [2, 4];
  if (positionId === 'root') return [3, 4];
  return [4];
}

export const chordQuestions: QuizQuestion[] = chordKeys.flatMap(([keyId, keyLabel]) => (
  chordDegrees.flatMap(([degreeId, degreeLabel, degreeName]) => (
    chordPositions.map(([positionId, positionLabel]) => {
      const id = `${keyId}-${degreeId}-${positionId}`;
      return {
        id,
        answer: `${keyLabel} ${degreeLabel} ${degreeName}・${positionLabel}`,
        choices: [],
        prompt: 'この和音はどれ？',
        subtitle: '五線譜の和音カードを見て選ぼう',
        levels: chordLevels(keyId, positionId),
        image: `/download/chord-cards/lilypond/${id}.cropped.svg`,
      };
    })
  ))
));

const keySignatureData = [
  ['natural', 0, 'ハ長調', 'イ短調'],
  ['sharps-1', 1, 'ト長調', 'ホ短調'],
  ['sharps-2', 2, 'ニ長調', 'ロ短調'],
  ['sharps-3', 3, 'イ長調', '嬰ヘ短調'],
  ['sharps-4', 4, 'ホ長調', '嬰ハ短調'],
  ['sharps-5', 5, 'ロ長調', '嬰ト短調'],
  ['sharps-6', 6, '嬰ヘ長調', '嬰ニ短調'],
  ['sharps-7', 7, '嬰ハ長調', '嬰イ短調'],
  ['flats-1', 1, 'ヘ長調', 'ニ短調'],
  ['flats-2', 2, '変ロ長調', 'ト短調'],
  ['flats-3', 3, '変ホ長調', 'ハ短調'],
  ['flats-4', 4, '変イ長調', 'ヘ短調'],
  ['flats-5', 5, '変ニ長調', '変ロ短調'],
  ['flats-6', 6, '変ト長調', '変ホ短調'],
  ['flats-7', 7, '変ハ長調', '変イ短調'],
] as const;

export const keySignatureQuestions: QuizQuestion[] = keySignatureData.flatMap(([id, count, major, minor]) => {
  const image = `/download/key-signature-cards/lilypond/${id}.svg`;
  const majorLevels: GameLevel[] = count <= 3 ? [1, 2, 4] : [2, 4];
  return [
    {
      id: `${id}-major`,
      answer: major,
      choices: [],
      prompt: 'この調号は何長調？',
      subtitle: 'シャープ・フラットの数と位置を見よう',
      levels: majorLevels,
      image,
    },
    {
      id: `${id}-minor`,
      answer: minor,
      choices: [],
      prompt: 'この調号は何短調？',
      subtitle: '平行調も少しずつ覚えよう',
      levels: [3, 4],
      image,
    },
    {
      id: `${id}-pair`,
      answer: `${major} / ${minor}`,
      choices: [],
      prompt: 'この調号は何調？',
      subtitle: '長調と短調の組み合わせで答えよう',
      levels: [4],
      image,
    },
  ];
});

export function shuffle<T>(items: T[]): T[] {
  return [...items].sort(() => Math.random() - 0.5);
}

export function pick<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}
